import React, { Component } from 'react';
import { func, object } from 'prop-types';
import { contains, findIndex, propEq, update } from 'ramda';

import { formatFormElementsForSubmission } from '../../../../../lib/FormUtil';

// Redux
import { connect } from 'react-redux';
import {
  closeAlertDialog, closeConfirmDialog, openConfirmDialog, openErrorDialog, openSuccessDialog
} from '../../../../../actions/dialog/dialogActions';
import { hideBackButton, setHeaderTitle, showBackButton } from '../../../../../actions/header/headerActions';
import { setMetadata } from '../../../../../actions/metadata/metadataActions';
import { execApi, execApiWithParam } from '../../../../../actions/api/apiActions';
import * as FormActionTypes from '../../../../../actions/forms/types';

// APIs
import FormsApi from '../../../../../apis/forms.js';

// Main Layout Components
import ActionsPanel from '../../../../ActionsPanel';
import ContentPanel from '../../../../ContentPanel';
import Layout from '../../../../Layout';
import LoadingState from '../../../../LoadingState';
import FormElementList from '../../FormElementList';
import PrintButton from '../../../../PrintButton';
import SecondaryMenuItem from '../../../../SecondaryMenu/SecondaryMenuItem';
import SecondaryMenu from '../../../../SecondaryMenu/SecondaryMenu';

// Page styles
import cx from 'classnames';
import s from '../../Forms.css';
import * as FormUtil from "../../../../../lib/FormUtil";


const draftIcon =
  <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28.8 28.8" className={s.draftIcon}>
    <g>
      <path className={s.draftIconNoFill} d="M4.4,9.7v12.9c0,1.1,0.9,2,2,2h12.9c1.1,0,2-0.9,2-2v-9l-7.5,7.5l-7.1,0.8l0.7-7.2l7.1-7.1H6.4C5.3,7.7,4.4,8.6,4.4,9.7z"/>
      <path className={s.draftIconFill} d="M21.3,22.6c0,1.1-0.9,2-2,2H6.4c-1.1,0-2-0.9-2-2V9.7c0-1.1,0.9-2,2-2h8.1l4-4H6.4c-3.3,0-6,2.7-6,6v12.9c0,3.3,2.7,6,6,6h12.9c3.3,0,6-2.7,6-6V9.7c0,0,0,0,0-0.1l-4,4V22.6z"/>
      <path className={s.draftIconFill}  d="M28.2,3.9c0.1-0.1,0.2-0.2,0.2-0.4c0-0.1-0.1-0.3-0.2-0.4l-2.8-2.8c-0.2-0.2-0.5-0.2-0.8,0L9.4,15.7l-0.4,4l3.9-0.4L28.2,3.9z"/>
    </g>
  </svg>;

export class LandingPage extends Component {
  static propTypes = {
    closeAlertDialog: func,
    closeConfirmDialog: func,
    execApi: func,
    execApiWithParam: func,
    header: object,
    hideHeaderBackButton: func,
    loadingState: object,
    openConfirmDialog: func,
    openErrorDialog: func,
    openSuccessDialog: func,
    setHeaderTitle: func,
    setMetadata: func,
    showHeaderBackButton: func,
  };

  clearFormState = {
    activeForm: {},
    activeFormKey: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      formHeaders: [],
      isLoading: true,
      ...this.clearFormState,
    };

    // ContentPanel
    this.contentPanel = null;

    this.setContentPanelRef = element => {
      this.contentPanel = element;
    };
  }

  componentWillMount() {
    this.props.setMetadata({
      activeMenuItemKey: 'forms',
      pageTitle: 'Forms',
      pageDescription: 'Fill out forms',
    });

    this.props.setHeaderTitle('Forms');
    this.getFormHeaders();
  };

  componentWillReceiveProps(nextProps) {
    if (!nextProps.header.backButton && this.state.activeFormKey) {
      this.goBack();
    }
  }

  getFormHeaders = () => {
    this.props.execApi({
      type: FormActionTypes.GET_FORM_HEADERS_PUBLIC,
      api: FormsApi.getPublicList,
      successCallback: response => {
        this.setState({ formHeaders: response.formHeaders });
      },
      errorCallback: () => {
        this.props.openErrorDialog({
          title: 'Error',
          content: 'Unable to load forms. Please try again later.',
        });
      }
    });
  };

  goBack = () => {
    this.setState({ ...this.clearFormState });
    if (this.props.header.backButton) {
      this.props.hideHeaderBackButton();
    }
  };

  clearFormValues = () => {
    if (!this.state.activeForm || !this.state.activeForm.formElements) {
      return;
    }

    const formElements = this.state.activeForm.formElements.map((formElement) => {
      formElement.value = '';
      return formElement;
    });

    this.setState({
      activeForm: {
        ...this.state.activeForm,
        formElements: formElements,
      }
    });
  };

  submitForm = () => {
    if (!this.validateUserInput()) {
      this.props.openErrorDialog({
        title: 'Missing Required Fields',
        content: 'Please fill out all the required fields that are marked with *',
      });
      return;
    }

    const elementsForSubmission = this.state.activeForm.formElements;
    formatFormElementsForSubmission(elementsForSubmission);

    const data = {
      formDetails: {
        id: this.state.activeForm.id,
        title: this.state.activeForm.title,
        formElements: elementsForSubmission,
        formScript: this.state.activeForm.formScript,
      },
    };

    this.props.execApiWithParam({
      type: FormActionTypes.SUBMIT_FORM_PUBLIC,
      api: FormsApi.submitPublicForm,
      param: data,
      successCallback: () => {
        this.clearFormValues();
        this.props.openSuccessDialog({
          title: 'Submitted Form',
          content: `We successfully submitted the information you provided for ${this.state.activeForm.title}`,
        });
      },
      errorCallback: error => {
        const message = error && error.message && error.message.body
          ? error.message.body
          : '';
        this.props.openErrorDialog({
          title: 'Error',
          content: 'We were unable to submit the information you provided.' + message + ' Please try again later',
        });
      }
    });
  };

  validateUserInput = () => {
    if (!this.state.activeForm || !this.state.activeForm.formElements) {
      return;
    }

    let isValid = true;
    const formElements = this.state.activeForm.formElements.map((element) => {
      if (element.required && !element.value) {
        isValid = false;
        element.error = 'This field is required';
      } else if (element.error) {
        element.error = '';
      }
      return element;
    });

    if (!isValid) {
      this.setState({
        activeForm: {
          ...this.state.activeForm,
          formElements: formElements,
        }
      });
    }

    return isValid;
  };

  hasRequiredFields = formElements => formElements.some(element => element.required);

  updateValue = (id, value) => {
    const formElementIndex = findIndex(propEq('id', id))(this.state.activeForm.formElements);
    if (formElementIndex < 0) {
      return;
    }

    const updatedFormElement = {
      ...this.state.activeForm.formElements[formElementIndex],
      value: value
    };

    this.setState({
      activeForm: {
        ...this.state.activeForm,
        formElements: update(formElementIndex, updatedFormElement, this.state.activeForm.formElements),
      }
    });
  };

  handleActiveMenuItem = (activeFormKey) => {
    this.props.execApiWithParam({
      type: FormActionTypes.GET_FORM_PUBLIC,
      api: FormsApi.getPublicForm,
      param: activeFormKey,
      successCallback: response => {
        let activeForm = response.formDetails;
        if (activeForm.formElements) {
          activeForm.hasRequiredElement = activeForm.formElements.reduce(
            (acc, ele) => acc || ele.required, false
          );
        }
        this.setState({
          activeFormKey: activeFormKey,
          activeForm: activeForm,
        });
        this.props.showHeaderBackButton();

        this.scrollToTopOfForm();
      },
      errorCallback: () => {
        this.props.openErrorDialog({
          title: 'Unable to Load Form',
          content: 'We are unable to load your form at this time. Please try again later.',
        });
      }
    });
  };

  renderFormMenu = () =>
    <SecondaryMenu id="public_forms_secondary_menu" className={s.secondaryMenu}>
      {this.state.formHeaders.map(header => (
        <SecondaryMenuItem
          active={header.id === this.state.activeFormKey}
          className={s.menuItem}
          id={`menu_item_${header.id}`}
          key={header.id}
          handleClick={() => this.handleActiveMenuItem(header.id)}
        >
          {header.draft && draftIcon}
          <div className={cx(s.menuTextBlock, !header.draft ? s.noIconPadding : '')}>
            <h1 className={s.menuTitle}>{header.title}</h1>
            {header.draft && <p className={s.menuSubText}>*Form is in draft mode</p>}
          </div>
        </SecondaryMenuItem>
      ))}
    </SecondaryMenu>;

  scrollToTopOfForm() {
    this.contentPanel.parentContainerRef.scrollTo(0,0);
  }

  render = () => {
    const { loadingState } = this.props;
    const hasRequiredFields = this.state.activeForm.formElements && this.hasRequiredFields(this.state.activeForm.formElements);
    const formState = this.state.formHeaders.length === 0 ?
      <p className={s.messagePlaceholder}>You have no forms.</p> :
      this.renderFormMenu();

    const submitFormButton =
      <button
        type="button"
        id="submit"
        className={s.btn}
        disabled={loadingState.isLoading}
        onClick={this.submitForm}
      >
        {contains(FormActionTypes.SUBMIT_FORM_PUBLIC, loadingState.requests) ?
          <LoadingState context="formSubmit"/> :
          <span>Submit</span>
        }
      </button>;
    return (
      <Layout>
        <ActionsPanel className={cx(s.actionsPanel, s.paddingTop, this.state.activeFormKey ? s.mobileHidden : '')}>
          {formState}
        </ActionsPanel>
        <ContentPanel className={cx(s.contentPanel, s.paddingTop, this.state.activeFormKey ? '' : s.mobileHidden)} ref={this.setContentPanelRef}>
          {this.state.activeFormKey ? (
            <div>
              <section className={s.utilitiesContentPanel}>
                <PrintButton id="public_forms_print" />
              </section>
              <form className={s.form}>

                <FormElementList
                  formElements={this.state.activeForm.formElements}
                  updateValue={(event, value) => this.updateValue(event, value)}
                  formId={this.state.activeFormKey}
                />

                {hasRequiredFields && <p>* Required field</p>}

                <div className={s.buttonRow}>
                  {submitFormButton}
                </div>
              </form>
            </div>
          ) : (
            <p className={s.placeholder}>
              Clicking a Form Title will display the Form here.
            </p>
          )}
        </ContentPanel>
      </Layout>
    )
  }
}

const mapStateToProps = state => ({
  header: state.header,
  loadingState: state.loadingState,
});

const mapDispatchToProps = dispatch => ({
  closeAlertDialog: () => dispatch(closeAlertDialog()),
  openErrorDialog: dialog => dispatch(openErrorDialog(dialog)),
  openSuccessDialog: dialog => dispatch(openSuccessDialog(dialog)),
  closeConfirmDialog: () => dispatch(closeConfirmDialog()),
  openConfirmDialog: dialog => dispatch(openConfirmDialog(dialog)),
  showHeaderBackButton: () => dispatch(showBackButton()),
  hideHeaderBackButton: () => dispatch(hideBackButton()),
  setHeaderTitle: title => dispatch(setHeaderTitle(title)),
  setMetadata: metadata => dispatch(setMetadata(metadata)),
  execApi: apiConfig => dispatch(execApi(apiConfig)),
  execApiWithParam: apiConfig => dispatch(execApiWithParam(apiConfig)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LandingPage);



// WEBPACK FOOTER //
// ./src/components/Pages/Forms/PublicForms/LandingPage/LandingPage.js